/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useRef, useState } from 'react';

/** How often the local clock ticks. */
const TICK_MS = 1000;

/**
 * Seconds left until *deadline*, floored at zero.
 *
 * Pure so the arithmetic can be tested without timers.
 */
export function remainingFrom(deadline: number | null, now: number): number | null {
  if (deadline === null) return null;
  return Math.max(0, Math.round((deadline - now) / 1000));
}

/**
 * A countdown that actually counts down.
 *
 * The board polls every 15 seconds, so a value rendered straight from the
 * server sits frozen between polls — on a screen whose most urgent number
 * is a deadline, that reads as a broken clock. This converts the server's
 * "seconds remaining" into a local deadline and re-derives the remainder
 * every second, re-syncing whenever a fresh board arrives.
 *
 * Derived from a deadline rather than decremented: a decrement drifts
 * whenever the tab is throttled or the interval fires late.
 *
 * @param seconds - Seconds remaining as reported by the server.
 * @returns Seconds remaining now, or null when the unit has no countdown.
 */
export function useCountdown(seconds: number | null | undefined): number | null {
  const deadlineRef = useRef<number | null>(null);
  const [value, setValue] = useState<number | null>(seconds ?? null);

  useEffect(() => {
    deadlineRef.current = seconds == null ? null : Date.now() + seconds * 1000;
    setValue(seconds ?? null);
  }, [seconds]);

  useEffect(() => {
    if (seconds == null) return undefined;
    const id = setInterval(
      () => setValue(remainingFrom(deadlineRef.current, Date.now())),
      TICK_MS,
    );
    return () => clearInterval(id);
  }, [seconds]);

  return value;
}
